import React from 'react';
import { Table, Button, Avatar, Tag, Typography, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import { UserOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { glassTableContainerStyle } from '../../Performance/utils';

const { Title, Text } = Typography;

interface RecentLeadsTableCardProps {
  leads: any[];
  tableLoading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  handleTableChange: (pagination: any) => void;
  navigate: any;
}

export const RecentLeadsTableCard: React.FC<RecentLeadsTableCardProps> = ({
  leads,
  tableLoading,
  pagination,
  handleTableChange,
  navigate,
}) => {
  // Helper functions for tag colors
  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      new: 'blue',
      baru: 'blue',
      contacted: 'green',
      qualified: 'orange',
      unqualified: 'red',
      converted: 'purple',
      closed: 'default',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      low: 'default',
      rendah: 'default',
      medium: 'orange',
      sedang: 'orange',
      high: 'red',
      tinggi: 'red',
    };
    return colors[priority] || 'default';
  };

  const leadColumns: TableProps<any>['columns'] = [
    {
      title: 'Nama Lengkap',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#7BAA18' }} />
          <Text strong style={{ color: '#1e293b' }}>{text}</Text>
        </div>
      ),
    },
    {
      title: 'Sumber Lead',
      dataIndex: ['lead_source', 'name'],
      key: 'lead_source',
      render: (text: string, record: any) => (
        <Tooltip title="Klik untuk memfilter leads dari sumber ini">
          <Tag
            color="blue"
            onClick={(e) => {
              e.stopPropagation();
              if (record.lead_source?.id) {
                navigate(`/leads/data?source=${record.lead_source.id}`);
              }
            }}
            style={{ borderRadius: '4px', fontWeight: '600', cursor: record.lead_source?.id ? 'pointer' : 'default' }}
          >
            {text || 'N/A'}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Property Area',
      dataIndex: ['property_area', 'name'],
      key: 'property_area',
      render: (text: string, record: any) => (
        <Tooltip title="Klik untuk memfilter leads di kawasan ini">
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (record.property_area?.id) {
                navigate(`/leads/data?property_area_id=${record.property_area.id}`);
              }
            }}
            style={{
              color: '#475569',
              fontWeight: '500',
              cursor: record.property_area?.id ? 'pointer' : 'default',
              textDecoration: record.property_area?.id ? 'underline dotted' : 'none',
            }}
          >
            {text || '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Tahapan',
      dataIndex: ['lead_status', 'name'],
      key: 'status',
      render: (status: string, record: any) => {
        const statusName = status?.toLowerCase() || '';
        return (
          <Tooltip title="Klik untuk memfilter leads pada tahapan ini">
            <Tag
              color={getStatusColor(statusName)}
              onClick={(e) => {
                e.stopPropagation();
                if (record.lead_status?.id) {
                  navigate(`/leads/data?tahapanFU=${record.lead_status.id}`);
                }
              }}
              style={{
                borderRadius: '4px',
                fontWeight: '600',
                textTransform: 'uppercase',
                cursor: record.lead_status?.id ? 'pointer' : 'default',
              }}
            >
              {status || 'N/A'}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Prioritas',
      dataIndex: ['lead_priority', 'name'],
      key: 'priority',
      render: (priority: string) => {
        const priorityName = priority?.toLowerCase() || '';
        return (
          <Tag color={getPriorityColor(priorityName)} style={{ borderRadius: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
            {priority || 'N/A'}
          </Tag>
        );
      },
    },
    {
      title: 'Tanggal Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => <Text style={{ color: '#64748b', fontWeight: '500' }}>{dayjs(date).format('DD MMM YYYY')}</Text>,
    },
    {
      title: 'Aksi',
      key: 'action',
      render: (_: any, record: any) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/leads/${record.id}`);
          }}
          style={{ color: '#7BAA18', fontWeight: '600' }}
        >
          Detail
        </Button>
      ),
    },
  ];

  return (
    <div style={glassTableContainerStyle} className="glass-table-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '0 8px' }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
            Leads Terbaru Masuk
          </Title>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Daftar penugasan prospek terbaru yang perlu diproses.
          </Text>
        </div>
        <Button
          type="primary"
          onClick={() => navigate('/leads/data')}
          icon={<EyeOutlined />}
          style={{
            backgroundColor: '#7BAA18',
            borderColor: '#7BAA18',
            borderRadius: '6px',
            fontWeight: '600',
          }}
        >
          Lihat Semua
        </Button>
      </div>

      <Table
        columns={leadColumns}
        dataSource={leads}
        loading={tableLoading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} leads`,
        }}
        onChange={handleTableChange}
        rowKey="id"
        scroll={{ x: 800 }}
        className="glass-table"
        locale={{ emptyText: 'Tidak ada data leads terbaru' }}
        onRow={(record) => ({
          onClick: () => navigate(`/leads/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

export default RecentLeadsTableCard;
